import React, { useEffect } from 'react';

const StrategyPattern = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Strategy Pattern - Behavioral Design Pattern Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Strategy Pattern - define family of algorithms and make them interchangeable for flexible algorithm selection. Learn with sorting and payment examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Strategy Pattern - define family of algorithms and make them interchangeable for flexible algorithm selection. Learn with sorting and payment examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'strategy pattern, design patterns, behavioral patterns, algorithm selection, polymorphism, software design');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'strategy pattern, design patterns, behavioral patterns, algorithm selection, polymorphism, software design';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Strategy Pattern</h1>
        <p style={styles.subtitle}>
          Behavioral Design Pattern - Make algorithms interchangeable
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Intent</h2>
          <p style={styles.paragraph}>
            The Strategy pattern defines a family of algorithms, encapsulates each one, and makes 
            them interchangeable. It lets the algorithm vary independently from clients that use it, 
            promoting flexibility and code reuse.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Problem</h2>
          <p style={styles.paragraph}>
            You have multiple ways to perform a task and want to choose the algorithm at runtime. 
            Using if-else statements or switch cases makes the code rigid and hard to extend. 
            The Strategy pattern provides a clean solution.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Example: Sorting Algorithms</h2>
          <div style={styles.codeBlock}>
{`// Strategy interface
interface SortingStrategy {
    void sort(int[] array);
    String getName();
}

// Concrete strategies
class BubbleSort implements SortingStrategy {
    @Override
    public void sort(int[] array) {
        System.out.println("Sorting using Bubble Sort");
        int n = array.length;
        for (int i = 0; i < n - 1; i++) {
            for (int j = 0; j < n - i - 1; j++) {
                if (array[j] > array[j + 1]) {
                    // Swap elements
                    int temp = array[j];
                    array[j] = array[j + 1];
                    array[j + 1] = temp;
                }
            }
        }
    }
    
    @Override
    public String getName() {
        return "Bubble Sort";
    }
}

class QuickSort implements SortingStrategy {
    @Override
    public void sort(int[] array) {
        System.out.println("Sorting using Quick Sort");
        quickSort(array, 0, array.length - 1);
    }
    
    private void quickSort(int[] array, int low, int high) {
        if (low < high) {
            int pi = partition(array, low, high);
            quickSort(array, low, pi - 1);
            quickSort(array, pi + 1, high);
        }
    }
    
    private int partition(int[] array, int low, int high) {
        int pivot = array[high];
        int i = (low - 1);
        
        for (int j = low; j < high; j++) {
            if (array[j] < pivot) {
                i++;
                int temp = array[i];
                array[i] = array[j];
                array[j] = temp;
            }
        }
        
        int temp = array[i + 1];
        array[i + 1] = array[high];
        array[high] = temp;
        
        return i + 1;
    }
    
    @Override
    public String getName() {
        return "Quick Sort";
    }
}

class MergeSort implements SortingStrategy {
    @Override
    public void sort(int[] array) {
        System.out.println("Sorting using Merge Sort");
        mergeSort(array, 0, array.length - 1);
    }
    
    private void mergeSort(int[] array, int left, int right) {
        if (left < right) {
            int middle = (left + right) / 2;
            mergeSort(array, left, middle);
            mergeSort(array, middle + 1, right);
            merge(array, left, middle, right);
        }
    }
    
    private void merge(int[] array, int left, int middle, int right) {
        int[] leftArray = Arrays.copyOfRange(array, left, middle + 1);
        int[] rightArray = Arrays.copyOfRange(array, middle + 1, right + 1);
        
        int i = 0, j = 0, k = left;
        
        while (i < leftArray.length && j < rightArray.length) {
            if (leftArray[i] <= rightArray[j]) {
                array[k++] = leftArray[i++];
            } else {
                array[k++] = rightArray[j++];
            }
        }
        
        while (i < leftArray.length) {
            array[k++] = leftArray[i++];
        }
        
        while (j < rightArray.length) {
            array[k++] = rightArray[j++];
        }
    }
    
    @Override
    public String getName() {
        return "Merge Sort";
    }
}

// Context class
class SortingContext {
    private SortingStrategy strategy;
    
    public SortingContext(SortingStrategy strategy) {
        this.strategy = strategy;
    }
    
    public void setStrategy(SortingStrategy strategy) {
        this.strategy = strategy;
    }
    
    public void executeStrategy(int[] array) {
        System.out.println("Using strategy: " + strategy.getName());
        long startTime = System.nanoTime();
        strategy.sort(array);
        long endTime = System.nanoTime();
        System.out.println("Time taken: " + (endTime - startTime) / 1000000 + " ms");
    }
}

// Usage
public class StrategyDemo {
    public static void main(String[] args) {
        int[] smallArray = {64, 34, 25, 12, 22, 11, 90};
        int[] mediumArray = generateRandomArray(1000);
        
        SortingContext context = new SortingContext(new BubbleSort());
        
        // Use bubble sort for small array
        System.out.println("Small array with Bubble Sort:");
        context.executeStrategy(smallArray.clone());
        
        // Switch to quick sort for medium array
        context.setStrategy(new QuickSort());
        System.out.println("\\nMedium array with Quick Sort:");
        context.executeStrategy(mediumArray.clone());
        
        // Switch to merge sort
        context.setStrategy(new MergeSort());
        System.out.println("\\nMedium array with Merge Sort:");
        context.executeStrategy(mediumArray.clone());
    }
    
    private static int[] generateRandomArray(int size) {
        Random random = new Random();
        return random.ints(size, 1, 1000).toArray();
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Key Benefits</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Flexibility:</strong> Can switch algorithms at runtime</li>
            <li style={styles.listItem}><strong>Open/Closed Principle:</strong> Easy to add new strategies</li>
            <li style={styles.listItem}><strong>Eliminates Conditionals:</strong> Replaces if-else chains</li>
            <li style={styles.listItem}><strong>Code Reuse:</strong> Strategies can be reused in different contexts</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>When you have multiple ways to perform a task</li>
            <li style={styles.listItem}>When you want to choose algorithm at runtime</li>
            <li style={styles.listItem}>When you want to avoid complex conditional statements</li>
            <li style={styles.listItem}>When you need to make algorithms interchangeable</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Strategy pattern provides a clean way to select algorithms at runtime while 
            maintaining flexibility and extensibility. It's particularly useful when you have 
            multiple approaches to solve the same problem.
          </p>
        </section>
      </main>
    </article>
  );
};

export default StrategyPattern;